import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/AuthProvider';
import { DataService, Organization } from './services/DataService';
import PermissionsManagement from './PermissionsManagement';
import CollectionManager from './CollectionManager';
import { 
  Building2, 
  Users, 
  Settings, 
  Database, 
  Shield, 
  ArrowLeft,
  Plus,
  Edit,
  Eye,
  Key
} from 'lucide-react';
import ApiAccessManagement from './ApiAccessManagement';

interface OrganizationDashboardProps {
  organizationId: string;
  onBack: () => void;
}

type ViewType = 'dashboard' | 'permissions' | 'collection' | 'api';

interface CollectionViewState {
  collectionId: string;
  collectionName: string;
  action: 'manage' | 'view';
}

const OrganizationDashboard: React.FC<OrganizationDashboardProps> = ({
  organizationId,
  onBack
}) => {
  // UPDATED: Get permissions from AuthProvider instead of loading them separately
  const { currentUser, userPermissions, fullUserPermissions } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [currentView, setCurrentView] = useState<ViewType>('dashboard');
  const [collectionView, setCollectionView] = useState<CollectionViewState | null>(null);

  // UPDATED: Only load organization data, permissions come from AuthProvider
  useEffect(() => {
    loadOrganizationData();
  }, [organizationId]);

  const loadOrganizationData = async () => {
    try {
      setLoading(true);
      const org = await DataService.getOrganization(organizationId);
      setOrganization(org);
    } catch (err: any) {
      console.error('Error loading organization:', err);
      setError('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  // UPDATED: Permission check functions now use AuthProvider data
  const isAppAdmin = (): boolean => {
    return userPermissions?.includes('APP_ADMIN') || false;
  };

  const isApiUser = (): boolean => {
    return isOrgAdmin() || (fullUserPermissions?.orgPermissions?.includes(`${organizationId}_api`) || false);
  };

  const isOrgAdmin = (): boolean => {
    return fullUserPermissions?.orgPermissions?.includes(`${organizationId}_admin`) || false;
  };

  const canManagePermissions = (): boolean => {
    return isAppAdmin() || isOrgAdmin();
  };

  const canManageApi = (): boolean => {
    return isApiUser();
  };

  const hasWriteAccess = (): boolean => {
    if (isAppAdmin() || isOrgAdmin()) return true;
    return fullUserPermissions?.orgPermissions?.includes(`${organizationId}_write`) || false;
  };

  const hasReadAccess = (): boolean => {
    if (isAppAdmin() || isOrgAdmin() || hasWriteAccess()) return true;
    return fullUserPermissions?.orgPermissions?.includes(`${organizationId}_read`) || false;
  };

  const getUserPermissionLevel = (): string => {
    if (isAppAdmin()) return 'APP_ADMIN';
    if (isOrgAdmin()) return 'admin';
    
    const orgPermissions = fullUserPermissions?.orgPermissions || [];
    if (orgPermissions.includes(`${organizationId}_write`)) return 'write';
    if (orgPermissions.includes(`${organizationId}_read`)) return 'read';
    if (orgPermissions.includes(`${organizationId}_api`)) return 'api';
    
    return 'none';
  };

  const getPermissionBadgeColor = (level: string): string => {
    switch (level) {
      case 'APP_ADMIN': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-red-100 text-red-800';
      case 'write': return 'bg-blue-100 text-blue-800';
      case 'read': return 'bg-green-100 text-green-800';
      case 'api': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCollectionAction = (collectionId: string, collectionName: string, action: 'manage' | 'view') => {
    setCollectionView({ collectionId, collectionName, action });
    setCurrentView('collection');
  };

  const handleManagePermissions = () => {
    setCurrentView('permissions');
  };

  const handleManageApi = () => {
    setCurrentView('api');
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setCollectionView(null);
  };

  const formatOrganizationName = (name: string): string => {
    return name
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Organization...</h2>
          <p className="text-gray-600">Please wait while we load the organization data</p>
        </div>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error || 'Organization not found'}
          </div>
          <button 
            onClick={onBack}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Render permissions management view
  if (currentView === 'permissions') {
    return (
      <PermissionsManagement
        organizationId={organizationId}
        onBack={handleBackToDashboard}
      />
    );
  }

  if (currentView === 'api') {
    return (
      <ApiAccessManagement
        organizationId={organizationId}
        onBack={handleBackToDashboard}
      />
    );
  }

  // Render collection management view
  if (currentView === 'collection' && collectionView) {
    return (
      <CollectionManager
        organizationId={organizationId}
        collectionId={collectionView.collectionId}
        collectionName={collectionView.collectionName}
        onBack={handleBackToDashboard}
      />
    );
  }

  // Render main dashboard
  const userPermissionLevel = getUserPermissionLevel();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Back to organizations"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Building2 className="w-6 h-6 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {formatOrganizationName(organization.name)}
                  </h1>
                  <p className="text-sm text-gray-500">ID: {organization.id}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPermissionBadgeColor(userPermissionLevel)}`}>
                {userPermissionLevel}
              </span>
              <span className="text-sm text-gray-500">
                {currentUser?.email}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Navigation</h2>
              
              <nav className="space-y-2">
                {/* Manage Permissions Button (if user has permission) */}
                {canManagePermissions() && (
                  <button
                    onClick={handleManagePermissions}
                    className="w-full flex items-center px-3 py-2 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors group"
                  >
                    <Shield className="w-5 h-5 mr-3 group-hover:text-blue-600" />
                    Manage Permissions
                  </button>
                )}

                {canManageApi() && (
                  <button
                    onClick={handleManageApi}
                    className="w-full flex items-center px-3 py-2 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors group"
                  >
                    <Shield className="w-5 h-5 mr-3 group-hover:text-blue-600" />
                    Manage API Access
                  </button>
                )}

                {/* Collection Management Buttons */}
                {organization.collections && organization.collections.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Collections</h3>
                    {organization.collections.map(collection => (
                      <div key={collection.id} className="space-y-1">
                        {hasReadAccess() && (
                          <button
                            onClick={() => handleCollectionAction(collection.id, collection.name, 'manage')}
                            className="w-full flex items-center px-3 py-2 text-left text-gray-700 hover:bg-green-50 hover:text-green-700 rounded-lg transition-colors group"
                          >
                            <Database className="w-5 h-5 mr-3 group-hover:text-green-600" />
                            {collection.name}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Settings */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    className="w-full flex items-center px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <Settings className="w-5 h-5 mr-3 group-hover:text-gray-600" />
                    Settings
                  </button>
                </div>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Organization Overview */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Organization Overview</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Collections */}
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <Database className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Collections</h3>
                  <p className="text-3xl font-bold text-blue-600 mt-1">
                    {organization.collections?.length || 0}
                  </p>
                </div>

                {/* Roles */}
                <div className="text-center">
                  <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Roles</h3>
                  <p className="text-3xl font-bold text-green-600 mt-1">
                    {organization.roles?.length || 0}
                  </p>
                </div>

                {/* Your Access */}
                <div className="text-center">
                  <div className="bg-purple-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <Key className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Your Access</h3>
                  <p className="text-lg font-semibold text-purple-600 mt-1">
                    {userPermissionLevel}
                  </p>
                </div>
              </div>
            </div>

            {/* Collections Details */}
            {organization.collections && organization.collections.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Collections</h2>
                
                <div className="grid gap-4">
                  {organization.collections.map(collection => (
                    <div key={collection.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium text-gray-900">{collection.name}</h3>
                            {collection.schema && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Schema Defined
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">ID: {collection.id}</p>
                          {collection.schema && (
                            <p className="text-xs text-gray-400 mt-1">
                              {Object.keys(collection.schema.fields).length} fields defined
                            </p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          {hasReadAccess() && (
                            <button
                              onClick={() => handleCollectionAction(collection.id, collection.name, 'view')}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </button>
                          )}
                          {hasWriteAccess() && (
                            <button
                              onClick={() => handleCollectionAction(collection.id, collection.name, 'manage')}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Manage
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            {(canManagePermissions() || hasWriteAccess()) && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {canManagePermissions() && (
                    <button
                      onClick={handleManagePermissions}
                      className="flex items-center justify-center px-6 py-4 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors group"
                    >
                      <Shield className="w-6 h-6 mr-3 group-hover:text-blue-700" />
                      <div className="text-left">
                        <div className="font-medium">Manage Permissions</div>
                        <div className="text-sm text-blue-500">Add or remove user access</div>
                      </div>
                    </button>
                  )}
                  
                  {hasWriteAccess() && organization.collections && organization.collections.length > 0 && (
                    <button 
                      onClick={() => {
                        const firstCollection = organization.collections![0];
                        handleCollectionAction(firstCollection.id, firstCollection.name, 'manage');
                      }}
                      className="flex items-center justify-center px-6 py-4 border-2 border-dashed border-green-300 rounded-lg text-green-600 hover:border-green-400 hover:bg-green-50 transition-colors group"
                    >
                      <Database className="w-6 h-6 mr-3 group-hover:text-green-700" />
                      <div className="text-left">
                        <div className="font-medium">Manage Data</div>
                        <div className="text-sm text-green-500">Add, edit, or view documents</div>
                      </div>
                    </button>
                  )}
                  
                  <button className="flex items-center justify-center px-6 py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors group">
                    <Settings className="w-6 h-6 mr-3 group-hover:text-gray-700" />
                    <div className="text-left">
                      <div className="font-medium">Organization Settings</div>
                      <div className="text-sm text-gray-500">Configure organization details</div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Roles Details */}
            {organization.roles && organization.roles.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6 mt-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Available Roles</h2>
                
                <div className="flex flex-wrap gap-2">
                  {organization.roles.map(role => (
                    <span 
                      key={role}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* No Access Message */}
            {!hasReadAccess() && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <div className="text-yellow-600 mb-2">
                  <Shield className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-yellow-800 mb-2">
                  Limited Access
                </h3>
                <p className="text-yellow-700">
                  You currently have {userPermissionLevel} access to this organization. 
                  Contact an administrator to request additional permissions.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDashboard;